import * as z from 'zod/mini'

// --- Field key: "{データ種類}_{データ分類}_{項目名}" (SR-005)
/** @typedef {string} FieldKey */

// --- Row state (SR-008)
export const RowStateSchema = z.enum(['IDLE', 'EDITING', 'SKIP', 'HAS_CUSTOM_RULE'])
/** @typedef {z.infer<typeof RowStateSchema>} RowState */

// --- Validation state (SR-010)
export const ValidationStateSchema = z.enum(['NO_RESULT', 'VALIDATING', 'HAS_RESULT'])
/** @typedef {z.infer<typeof ValidationStateSchema>} ValidationState */

// --- Current filter (SR-011)
export const CurrentFilterSchema = z.enum(['all', 'error', 'warning'])
/** @typedef {z.infer<typeof CurrentFilterSchema>} CurrentFilter */

// --- Mapping mode (priority / sum)
export const MappingModeSchema = z.enum(['priority', 'sum'])
/** @typedef {z.infer<typeof MappingModeSchema>} MappingMode */

// --- Mapping item (one entry in items array); structure per spec §7.5.3
const MappingItemSchema = z.looseObject({
  type: z.string(),
  fileType: z.optional(z.string()),
  field: z.optional(z.string())
})
/** @typedef {z.infer<typeof MappingItemSchema>} MappingItem */

// --- mappingData[fieldKey] shape (SR-004, §7.5.3, §7.9.4)
export const MappingDataEntrySchema = z.object({
  items: z.array(MappingItemSchema),
  mode: MappingModeSchema
})
/** @typedef {z.infer<typeof MappingDataEntrySchema>} MappingDataEntry */

// --- mappingData: Record<fieldKey, { items, mode }>
/** @typedef {Record<FieldKey, MappingDataEntry>} MappingData */

// --- skippedFields: Record<fieldKey, true> (SR-006)
/** @typedef {Record<FieldKey, true>} SkippedFields */

// --- URL params
export const UrlParamsSchema = z.object({
  dataCreateTaskKey: z.string(),
  genericKindCode: z.string()
})
/** @typedef {z.infer<typeof UrlParamsSchema>} UrlParams */

// --- Meta (SR-002): 画面メタ情報（汎用タスク・データ種類・期間・previousTaskCode 等）
export const MetaSchema = z.looseObject({
  genericMappingTask: z.looseObject({
    genericTaskCode: z.string(),
    genericTaskName: z.string(),
    genericTaskKindName: z.optional(z.string()),
    genericTaskDescription: z.optional(z.string())
  }),
  genericChrofyColumns: z.array(
    z.looseObject({
      genericTaskCode: z.string(),
      chrofyColumnCode: z.string(),
      chrofyColumnName: z.string(),
      chrofyColumnDataType: z.optional(z.string()),
      chrofyColumnDescription: z.optional(z.string()),
      chrofyColumnSample: z.optional(z.string()),
      valueMappingColumns: z.optional(z.array(z.string())),
      hasJudgmentRule: z.optional(z.boolean()),
      isRequired: z.optional(z.boolean()),
      genericDataKindName: z.optional(z.string())
    })
  ),
  dataCreateTaskInfo: z.optional(
    z.looseObject({
      key: z.number(),
      previousTaskCode: z.optional(z.string()),
      periodKind: z.optional(z.string()),
      periodValue: z.optional(z.string()),
      taskStatus: z.optional(z.string()),
      dataCreateStatus: z.optional(z.string()),
      dataFixStatus: z.optional(z.string()),
      dataFixDateTime: z.optional(z.string())
    })
  ),
  conversionTableList: z.array(
    z.looseObject({
      conversionTableKey: z.string(),
      conversionTableName: z.optional(z.string())
    })
  )
})
/** @typedef {z.infer<typeof MetaSchema>} Meta */

// --- Generic column with hasCustomRule (IF02085), hasRule (IF02083 hasJudgmentRule), SR-008
export const GenericColumnWithRuleSchema = z.looseObject({
  genericTaskCode: z.string(),
  chrofyColumnCode: z.string(),
  chrofyColumnName: z.string(),
  hasCustomRule: z.boolean(),
  hasRule: z.optional(z.boolean()),
  isRequired: z.optional(z.boolean())
})
/** @typedef {z.infer<typeof GenericColumnWithRuleSchema>} GenericColumnWithRule */

// --- Validation summary (SR-013)
export const ValidationSummarySchema = z.looseObject({
  totalRecords: z.number(),
  successCount: z.number(),
  errorCount: z.number(),
  warningCount: z.number(),
  lastExecutionDateTime: z.optional(z.nullable(z.string())),
  lastExecutionUserName: z.optional(z.nullable(z.string()))
})
/** @typedef {z.infer<typeof ValidationSummarySchema>} ValidationSummary */

// --- Validation result row (one row in mapping result table)
export const ValidationResultRowSchema = z.looseObject({
  rowNumber: z.optional(z.number()),
  employeeId: z.optional(z.string()),
  columns: z.optional(z.record(z.string(), z.unknown()))
})
/** @typedef {z.infer<typeof ValidationResultRowSchema>} ValidationResultRow */

// --- Error detail item (SR-013, ⑤-5)
export const ErrorDetailItemSchema = z.looseObject({
  sourceFileName: z.optional(z.string()),
  rowNumber: z.optional(z.number()),
  columnName: z.optional(z.string()),
  currentValue: z.optional(z.string()),
  errorType: z.optional(z.string()),
  message: z.optional(z.string()),
  suggestion: z.optional(z.string())
})
/** @typedef {z.infer<typeof ErrorDetailItemSchema>} ErrorDetailItem */

// --- Validation gate (IF02086): BE 側で「検証実行可否」を判定して返すゲート情報
// `can_validate: false` のとき、`reason` に理由文字列が入る（例: 'data_create_in_progress' / 'task_not_found'）
export const ValidationGateSchema = z.looseObject({
  canValidate: z.boolean(),
  reason: z.optional(z.union([z.string(), z.null()]))
})
/** @typedef {z.infer<typeof ValidationGateSchema>} ValidationGate */

// --- Validation result API response (IF02086)
export const ValidationResultResponseSchema = z.looseObject({
  summary: ValidationSummarySchema,
  results: z.optional(
    z.looseObject({
      warnings: z.optional(z.array(ErrorDetailItemSchema)),
      errors: z.optional(z.array(ErrorDetailItemSchema))
    })
  ),
  dataMappingPreview: z.optional(z.union([z.string(), z.array(ValidationResultRowSchema)])),
  validationGate: z.optional(ValidationGateSchema)
})
/** @typedef {z.infer<typeof ValidationResultResponseSchema>} ValidationResultResponse */

// --- File type info (IF02084, SR-050)
export const FileTypeInfoSchema = z.looseObject({
  fileId: z.string(),
  mappingFileType: z.string(),
  headers: z.optional(z.array(z.string())),
  columnUniqueValues: z.optional(z.record(z.string(), z.array(z.unknown()))),
  sampleRows: z.optional(z.array(z.unknown()))
})
/** @typedef {z.infer<typeof FileTypeInfoSchema>} FileTypeInfo */
